import { getBorderThreshold } from './get-border-threshold.js';
import { markersReducer } from './utils.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { ThresholdBar } from '../../../core/components/threshold-bar/ThresholdBar.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useGetThresholds } from '../../hooks/useGetThresholds.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { useSetState, useZoomState } from '../../store/selectors.js';
import { AREAS } from '../../utils/grid-templates.js';

export const XYChartThresholdArea = () => {
  const { thresholds, none } = useGetThresholds();
  const { left, right } = useVisibleAxesConfig();
  const axesIdToScales = useXYChartScales();
  const axesIdToFormatter = useAxesFormatter();
  const { width } = useGridPlotSize();
  const { left: leftConfig, right: rightConfig } = useVisibleAxesConfig();
  const setState = useSetState();
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();

  const isZoomed = zoomInEnabled && zoomOutEnabled;
  const isMaxZoomed = !zoomInEnabled && zoomOutEnabled;
  const isAnyZoomApplied = isZoomed || isMaxZoomed;

  if (none) {
    return null;
  }

  const leftMarkers = thresholds.reduce(
    markersReducer({
      axis: 'left',
      axisConfig: leftConfig,
      axesIdToScales,
      axesIdToFormatter,
      width,
    }),
    [],
  );

  const rightMarkers = thresholds.reduce(
    markersReducer({
      axis: 'right',
      axisConfig: rightConfig,
      axesIdToScales,
      axesIdToFormatter,
      width,
    }),
    [],
  );

  const handleHover = (id: string | undefined) => {
    setState((prev) => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        hoveredThresholdId: id,
      },
    }));
  };

  const { leftBorder, rightBorder } = getBorderThreshold(left, right);

  return (
    <>
      {leftMarkers.length ? (
        <ChartGrid.GridArea gridArea={AREAS.leftThreshold} key="leftThreshold">
          {(args) => {
            return (
              <ThresholdBar
                borderArea={leftBorder}
                markers={isAnyZoomApplied ? [] : leftMarkers}
                length={args.availableHeight}
                layout="vertical"
                onHover={handleHover}
              />
            );
          }}
        </ChartGrid.GridArea>
      ) : null}
      {rightMarkers.length ? (
        <ChartGrid.GridArea
          gridArea={AREAS.rightThreshold}
          key="rightThreshold"
        >
          {(args) => {
            return (
              <ThresholdBar
                borderArea={rightBorder}
                markers={isAnyZoomApplied ? [] : rightMarkers}
                length={args.availableHeight}
                layout="vertical"
                onHover={handleHover}
              />
            );
          }}
        </ChartGrid.GridArea>
      ) : null}
    </>
  );
};

XYChartThresholdArea['displayName'] = 'XYChartThresholdArea';
